// ────────────────────────────────────────────────────────────────────
// WorklistContext — shared filter/group/expand state for the asset worklist
// (AssetNavPanel), so the inspector's view follows them across the Site Map
// and asset-workspace screens instead of resetting on every navigation.
//
// One bucket, scoped to a plant: `useWorklist(plantId)` resets the bucket to
// defaults only when plantId changes to a *different* non-null plant. Passing
// undefined (asset page mid-load) never resets, so the view carries over from
// the Site Map until the asset's real plant id confirms the same scope.
// ────────────────────────────────────────────────────────────────────

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

export type Discipline = "piping" | "static" | "tank" | "other";

interface WorklistValue {
  scope: number | null;
  reset: (plantId: number) => void;
  // Sidebar collapse — a viewing preference shared across screens (so a collapse
  // on one follows to the other); intentionally NOT cleared by reset().
  collapsed: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  search: string;
  setSearch: (v: string) => void;
  cls: string;
  setCls: (v: string) => void;
  sortBy: "risk" | "tag";
  setSortBy: React.Dispatch<React.SetStateAction<"risk" | "tag">>;
  grouped: boolean;
  setGrouped: React.Dispatch<React.SetStateAction<boolean>>;
  expanded: Set<string> | null;
  setExpanded: React.Dispatch<React.SetStateAction<Set<string> | null>>;
  topOpen: Set<Discipline>;
  setTopOpen: React.Dispatch<React.SetStateAction<Set<Discipline>>>;
}

const WorklistCtx = createContext<WorklistValue | null>(null);

export function WorklistProvider({ children }: { children: ReactNode }): JSX.Element {
  const [scope, setScope] = useState<number | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [search, setSearch] = useState("");
  const [cls, setCls] = useState<string>("ALL");
  const [sortBy, setSortBy] = useState<"risk" | "tag">("risk");
  const [grouped, setGrouped] = useState(true);
  const [expanded, setExpanded] = useState<Set<string> | null>(null);
  const [topOpen, setTopOpen] = useState<Set<Discipline>>(() => new Set<Discipline>(["piping", "static", "tank"]));

  const reset = useCallback((plantId: number): void => {
    setScope(plantId);
    setSearch("");
    setCls("ALL");
    setSortBy("risk");
    setGrouped(true);
    setExpanded(null);
    setTopOpen(new Set<Discipline>(["piping", "static", "tank"]));
  }, []);

  return (
    <WorklistCtx.Provider
      value={{
        scope, reset, collapsed, setCollapsed,
        search, setSearch, cls, setCls, sortBy, setSortBy,
        grouped, setGrouped, expanded, setExpanded, topOpen, setTopOpen,
      }}
    >
      {children}
    </WorklistCtx.Provider>
  );
}

/**
 * Read the shared worklist state. Pass the current plant id so the bucket resets
 * when the inspector moves to a different plant; pass undefined to leave it as-is
 * (the asset page does this while its asset — hence plant id — is still loading).
 */
export function useWorklist(plantId?: number): WorklistValue {
  const ctx = useContext(WorklistCtx);
  if (!ctx) throw new Error("useWorklist must be used within a WorklistProvider");
  const { scope, reset } = ctx;
  useEffect(() => {
    if (plantId != null && scope !== plantId) reset(plantId);
  }, [plantId, scope, reset]);
  return ctx;
}

/** Shared sidebar collapse state — separate from useWorklist so the sidebar
 *  chrome can read it without triggering the plant-scope reset effect. */
export function useSidebarCollapsed(): [boolean, React.Dispatch<React.SetStateAction<boolean>>] {
  const ctx = useContext(WorklistCtx);
  if (!ctx) throw new Error("useSidebarCollapsed must be used within a WorklistProvider");
  return [ctx.collapsed, ctx.setCollapsed];
}
